import { useApolloClient } from "@apollo/client";
import { Spacer } from "@hackthenorth/north";
import debounce from "lodash.debounce";
import moment from "moment";
import { Button, Label, Table, Text, TextInput } from "north.js";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";

import { Modal } from "src/shared/components";
import Autocomplete from "src/shared/components/Autocomplete";
import { errorToast, successToast } from "src/shared/components/Toast";
import { EVENT_ID } from "src/shared/constants/event";
import { useUserContext } from "src/shared/contexts";
import { deviceBreakpoints } from "src/theme/deviceBreakpoints";
import { ToggleSlider } from "src/views/schedule/components/ToggleSlider";
import { parseMaxCountRestriction } from "src/views/volunteer/shift-schedule/utils";

import { BufferLearnMoreLink } from "../../../buffer-information";
import {
  VolunteerAssignedShiftsDocument,
  VolunteerAssignedShiftsQuery,
  VolunteerAssignedShiftsQueryVariables,
} from "../../../check-in/graphql/volunteerAssignedShifts.generated";
import { useGetShiftSignupsSelfQuery } from "../../../home/graphql/getShiftSignupsSelf.generated";
import { useGetVolunteerShiftAvailabilityQuery } from "../../../shift-schedule/graphql/getVolunteerShiftAvailability.generated";
import type { TIsDisabled, TIsRegistered } from "../../../shift-schedule/types";
import { useUpdateVolunteerShiftMutation } from "../../../shifts/graphql/updateVolunteerShift.generated";
import {
  useVolunteerLazyQuery,
  VolunteerDocument,
} from "../../../shifts/graphql/volunteer.generated";
import type {
  VolunteerQuery,
  VolunteerQueryVariables,
} from "../../../shifts/graphql/volunteer.generated";
import { useVolunteerShiftsQuery } from "../../../shifts/graphql/volunteerShifts.generated";
import type { VolunteerShiftSignupsQuery } from "../../../shifts/graphql/volunteerShiftSignups.generated";
import { useVolunteerShiftSignupsQuery } from "../../../shifts/graphql/volunteerShiftSignups.generated";
import { QueryMode } from "../../constants/prisma";
import {
  BUFFER_MINUTES,
  getBufferedShift,
  projectWithCandidate,
  TBufferShift,
} from "../../utils/buffer";
import {
  formatVolunteerShiftDay,
  getVolunteerShiftHourAndMinute,
} from "../../utils/date";

import { useCreateShiftSignupMutation } from "./graphql/createShiftSignup.generated";
import { useDeleteVolunteerShiftSignupMutation } from "./graphql/deleteVolunteerShiftSignup.generated";
import { useUpdateShiftSignupMutation } from "./graphql/updateShiftSignup.generated";

import "twin.macro";

type Props = {
  shiftId: number;
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  onDeleteShift?: () => void;
  onChange?: () => void;
  // signup state and actions are owned by the shift schedule, which already
  // computes them for the list view; without them the modal is read-only
  isRegistered?: TIsRegistered;
  isDisableRegistered?: TIsDisabled;
  onRegister?: () => void;
  onUnregister?: () => void;
  signupCount?: number;
};

type VolunteerSignup = NonNullable<
  VolunteerShiftSignupsQuery["volunteerShiftSignups"]
>[number];

type ConflictingShift = {
  shiftId: number;
  name: string;
  start: Date;
  end: Date;
};

type PendingConflictAssign = {
  eventAttendeeId: number;
  volunteerName: string;
  conflicts: ConflictingShift[];
};

const overlaps = (aStart: Date, aEnd: Date, bStart: Date, bEnd: Date) =>
  aStart < bEnd && aEnd > bStart;

const formatConflictShift = (shift: ConflictingShift) =>
  `${shift.name} (${formatVolunteerShiftDay(
    shift.start
  )} ${getVolunteerShiftHourAndMinute(
    shift.start
  )} – ${getVolunteerShiftHourAndMinute(shift.end)})`;

const PRIMARY_SHIFTS = [
  "Activities",
  "Workshops",
  "Food",
  "Ceremonies",
  "Floor Patrol",
  "Security",
];

const STATUS_LABELS = [
  "Registered",
  "Checked in",
  "Completed",
  "Incomplete",
] as const;
type StatusLabel = typeof STATUS_LABELS[number];

const getStatusColor = (status: StatusLabel) => {
  switch (status) {
    case "Checked in":
      return "secondary-light";
    case "Incomplete":
      return "danger-light";
    case "Completed":
      return "success-light";
    default:
      return "primary-light";
  }
};

const VolunteerShiftModal = ({
  shiftId,
  open,
  setOpen,
  onChange,
  isRegistered,
  isDisableRegistered,
  onRegister,
  onUnregister,
  signupCount,
}: Props) => {
  const [createShift] = useCreateShiftSignupMutation();
  const [deleteShiftSignup] = useDeleteVolunteerShiftSignupMutation();
  const [updateStatus, updateStatusResult] = useUpdateShiftSignupMutation();
  const [updateVolunteerShift, { loading: updatingRegistration }] =
    useUpdateVolunteerShiftMutation();

  const navigate = useNavigate();

  const {
    data: volunteerShiftData,
    error,
    loading,
    refetch: refetchVolunteerShift,
  } = useVolunteerShiftsQuery({
    variables: { where: { shift_id: { equals: shiftId } } },
  });
  const volunteerShift = volunteerShiftData?.volunteerShifts[0];

  const { isOrganizer } = useUserContext();

  const handleToggleRegistration = async () => {
    if (!volunteerShift) return;
    const nextOpen = !volunteerShift.registration_open;
    try {
      await updateVolunteerShift({
        variables: {
          id: shiftId,
          data: { registration_open: nextOpen },
        },
      });
      successToast(
        nextOpen ? "Registration opened for this shift" : "Registration closed"
      );
      await refetchVolunteerShift();
      onChange?.();
    } catch {
      errorToast("Couldn't update registration. Try again.");
    }
  };

  // the roster query is guarded by canManageVolunteers on the server, so only
  // organizers can run it
  const { data: shiftSignupsData, refetch: refetchVolunteerSignups } =
    useVolunteerShiftSignupsQuery({
      variables: { where: { shift_id: { equals: shiftId } } },
      skip: !isOrganizer,
    });

  const { data: availabilityData, error: availabilityError } =
    useGetVolunteerShiftAvailabilityQuery({
      variables: { event_id: volunteerShift?.shift.event_id ?? EVENT_ID },
      skip:
        signupCount !== undefined || isOrganizer || !open || !volunteerShift,
    });

  const { data: mySignupsData } = useGetShiftSignupsSelfQuery({
    variables: { event_id: EVENT_ID },
    skip: isOrganizer,
  });

  const filledSpots =
    signupCount ??
    (availabilityData
      ? availabilityData.volunteerShiftAvailability.find(
          (availability) => availability.shift_id === shiftId
        )?.signup_count ?? 0
      : undefined);

  const client = useApolloClient();
  const [getVolunteersList, { data: volunteersListData }] =
    useVolunteerLazyQuery();

  const [volunteerId, setVolunteerId] = useState<number | null>(null);
  const [isAssigning, setIsAssigning] = useState(false);
  const [menuOpenId, setMenuOpenId] = useState<number | null>(null);
  const [pendingConflict, setPendingConflict] =
    useState<PendingConflictAssign | null>(null);
  const [confirmRemoveConflict, setConfirmRemoveConflict] = useState(false);

  const isSignedUp = isRegistered?.registered ?? false;
  // being registered outranks the lock, the same order the old register button used
  const lockReason =
    !isSignedUp && isDisableRegistered?.disabled
      ? isDisableRegistered.reason
      : "";
  const isLocked = !isSignedUp && (isDisableRegistered?.disabled ?? false);

  const startDate = new Date(volunteerShift?.shift.start_time ?? 0);
  const endDate = new Date(volunteerShift?.shift.end_time ?? 0);
  const startDateStr = formatVolunteerShiftDay(startDate);
  const endDateStr = formatVolunteerShiftDay(endDate);
  const startTimeStr = getVolunteerShiftHourAndMinute(startDate);
  const endTimeStr = getVolunteerShiftHourAndMinute(endDate);
  const isSameDay = startDateStr === endDateStr;

  const myBufferShifts: TBufferShift[] = useMemo(
    () =>
      (mySignupsData?.myVolunteerShiftSignups ?? []).map((signup) => ({
        shiftId: signup.shift_id,
        start: new Date(signup.shift.start_time),
        end: new Date(signup.shift.end_time),
        checkedIn: signup.checked_in,
      })),
    [mySignupsData]
  );

  const bufferedForViewer = useMemo(() => {
    if (!volunteerShift?.shift.start_time || !volunteerShift?.shift.end_time) {
      return undefined;
    }

    const candidate: TBufferShift = {
      shiftId,
      start: new Date(volunteerShift.shift.start_time),
      end: new Date(volunteerShift.shift.end_time),
    };

    // Project as if signed up so browse + confirm match; if already signed up
    // projectWithCandidate just replaces the existing entry.
    const projected = projectWithCandidate(myBufferShifts, candidate);
    return getBufferedShift(shiftId, projected);
  }, [myBufferShifts, shiftId, volunteerShift]);

  const effectiveEndTimeStr = bufferedForViewer
    ? getVolunteerShiftHourAndMinute(bufferedForViewer.effectiveEnd)
    : endTimeStr;
  const showPersonalBuffer = !isOrganizer && !!bufferedForViewer?.hasBuffer;
  const [searchQuery, setSearchQuery] = useState("");

  // volunteer list fetch is organizer/lead-only
  useEffect(() => {
    if (!isOrganizer || searchQuery) return;
    getVolunteersList({
      variables: {
        where: {},
      },
    });
  }, [getVolunteersList, isOrganizer, searchQuery]);

  const debouncedSearchUsers = useMemo(
    () =>
      debounce((value: string) => {
        if (!isOrganizer) return;
        getVolunteersList({
          variables: {
            where: {
              event_attendee: {
                is: {
                  user: {
                    is: {
                      OR: [
                        {
                          email: {
                            contains: value,
                            mode: QueryMode.Insensitive,
                          },
                        },
                        {
                          name: {
                            contains: value,
                            mode: QueryMode.Insensitive,
                          },
                        },
                      ],
                    },
                  },
                },
              },
            },
          },
        });
      }, 500),
    [getVolunteersList, isOrganizer]
  );

  const assignedUserIds = useMemo(
    () =>
      new Set(
        (shiftSignupsData?.volunteerShiftSignups ?? []).map(
          (signup) => signup.event_attendee.user.id
        )
      ),
    [shiftSignupsData]
  );

  // filter out already signed up volunteers
  const userOptions = useMemo(
    () =>
      volunteersListData?.volunteer
        .filter((v) => !assignedUserIds.has(v.event_attendee.user.id))
        .slice(0, 20)
        .map((v) => ({
          label: `${v.event_attendee.user.name} (${v.event_attendee.user.email})`,
          value: v.event_attendee.user.id,
        })),
    [volunteersListData, assignedUserIds]
  );

  // Look up the volunteer and assign them in a single flow. The lookup result is
  // awaited here rather than read from shared state.
  const completeAssign = useCallback(
    async (eventAttendeeId: number) => {
      await createShift({
        variables: {
          data: {
            event_attendee_id: eventAttendeeId,
            shift_id: shiftId,
            checked_in: false,
          },
        },
      });
      await refetchVolunteerSignups();
      onChange?.();
      setVolunteerId(null);
    },
    [createShift, shiftId, refetchVolunteerSignups, onChange]
  );

  const clearConflictPrompt = useCallback(() => {
    setPendingConflict(null);
    setConfirmRemoveConflict(false);
  }, []);

  const handleAddVolunteer = useCallback(async () => {
    if (!volunteerId || isAssigning || !volunteerShift?.shift) return;

    // Catches the case where someone else assigned this volunteer while the
    // dropdown selection was sitting open.
    if (assignedUserIds.has(volunteerId)) {
      errorToast("This volunteer is already assigned to this shift.");
      setVolunteerId(null);
      return;
    }

    setIsAssigning(true);
    try {
      const { data } = await client.query<
        VolunteerQuery,
        VolunteerQueryVariables
      >({
        query: VolunteerDocument,
        variables: {
          where: {
            event_attendee: {
              is: { user: { is: { id: { equals: volunteerId } } } },
            },
          },
        },
        // Bypass the cache so a repeat lookup still resolves, and so the
        // assignment is made against current data.
        fetchPolicy: "network-only",
      });

      const volunteer = data?.volunteer?.[0];
      const eventAttendeeId = volunteer?.event_attendee_id;

      if (!eventAttendeeId) {
        errorToast("We couldn't find that volunteer. Please try again.");
        return;
      }

      const { data: assignedData } = await client.query<
        VolunteerAssignedShiftsQuery,
        VolunteerAssignedShiftsQueryVariables
      >({
        query: VolunteerAssignedShiftsDocument,
        variables: { event_attendee_id: eventAttendeeId },
        fetchPolicy: "network-only",
      });

      const targetStart = new Date(volunteerShift.shift.start_time);
      const targetEnd = new Date(volunteerShift.shift.end_time);
      const conflicts = (assignedData.volunteerShiftSignups ?? [])
        .filter((signup) => {
          if (signup.shift_id === shiftId) return false;
          return overlaps(
            new Date(signup.shift.start_time),
            new Date(signup.shift.end_time),
            targetStart,
            targetEnd
          );
        })
        .map((signup) => ({
          shiftId: signup.shift_id,
          name: signup.shift.name,
          start: new Date(signup.shift.start_time),
          end: new Date(signup.shift.end_time),
        }));

      if (conflicts.length > 0) {
        setPendingConflict({
          eventAttendeeId,
          volunteerName: volunteer.event_attendee.user.name,
          conflicts,
        });
        setConfirmRemoveConflict(false);
        return;
      }

      await completeAssign(eventAttendeeId);
    } catch (e) {
      console.error(e);
      errorToast("We couldn't assign that volunteer. Please try again.");
    } finally {
      setIsAssigning(false);
    }
  }, [
    volunteerId,
    isAssigning,
    volunteerShift,
    assignedUserIds,
    client,
    shiftId,
    completeAssign,
  ]);

  const handleConfirmRemoveConflicts = useCallback(async () => {
    if (!pendingConflict || isAssigning) return;

    setIsAssigning(true);
    try {
      for (const conflict of pendingConflict.conflicts) {
        await deleteShiftSignup({
          variables: {
            shift_id: conflict.shiftId,
            event_attendee_id: pendingConflict.eventAttendeeId,
          },
        });
      }
      await completeAssign(pendingConflict.eventAttendeeId);
      clearConflictPrompt();
    } catch (e) {
      console.error(e);
      errorToast("We couldn't resolve the conflict. Please try again.");
    } finally {
      setIsAssigning(false);
    }
  }, [
    pendingConflict,
    isAssigning,
    deleteShiftSignup,
    completeAssign,
    clearConflictPrompt,
  ]);

  const handleAssignDespiteConflicts = useCallback(async () => {
    if (!pendingConflict || isAssigning) return;

    setIsAssigning(true);
    try {
      await completeAssign(pendingConflict.eventAttendeeId);
      clearConflictPrompt();
    } catch (e) {
      console.error(e);
      errorToast("We couldn't assign that volunteer. Please try again.");
    } finally {
      setIsAssigning(false);
    }
  }, [pendingConflict, isAssigning, completeAssign, clearConflictPrompt]);

  const handleDeleteVolunteer = useCallback(
    async (shiftId: number, eventAttendeeId: number) => {
      try {
        await deleteShiftSignup({
          variables: { shift_id: shiftId, event_attendee_id: eventAttendeeId },
        });
        await refetchVolunteerSignups();
        onChange?.();
      } catch (e) {
        console.error(e);
      }
    },
    [deleteShiftSignup, refetchVolunteerSignups, onChange]
  );

  useEffect(() => {
    // Roster refetch is organizer-only
    if (!isOrganizer) return;
    if (open) {
      refetchVolunteerSignups();
    } else if (updateStatusResult.data) {
      refetchVolunteerSignups();
      onChange?.();
    }
  }, [
    updateStatusResult.data,
    refetchVolunteerSignups,
    onChange,
    open,
    isOrganizer,
  ]);

  const handleCloseModal = useCallback(() => {
    setOpen(false);
    setVolunteerId(null);
  }, [setOpen]);

  const columns = useMemo(
    () => [
      { id: "name", name: "Name" },
      { id: "status", name: "status" },
      { id: "actions", name: "actions", hideLabel: true },
    ],
    []
  );

  const StatusCell: React.FC<{
    signup: VolunteerSignup;
    menuOpenId: number | null;
    setMenuOpenId: React.Dispatch<React.SetStateAction<number | null>>;
  }> = ({ signup, menuOpenId, setMenuOpenId }) => {
    const now = moment().tz("America/New_York");
    const eventEnded = moment(volunteerShift?.shift.end_time).isBefore(now);

    const ref = useRef<HTMLDivElement>(null);
    const isOpen = menuOpenId === signup.event_attendee_id;
    const [anchorPos, setAnchorPos] = useState({ top: 0, left: 0 });

    const currentStatus: StatusLabel = useMemo(() => {
      if (signup.checked_in) {
        return eventEnded ? "Completed" : "Checked in";
      }
      if (eventEnded) return "Incomplete";
      return "Registered";
    }, [signup, eventEnded]);

    const dropdownLabels: StatusLabel[] = eventEnded
      ? ["Completed", "Incomplete"]
      : ["Registered", "Checked in"];

    const toggleMenu = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      setAnchorPos({ top: rect.bottom, left: rect.right + rect.width });
      setMenuOpenId(isOpen ? null : signup.event_attendee_id);
    };

    const handleStatusChange = async (newStatus: StatusLabel) => {
      try {
        const checkedIn =
          newStatus === "Checked in" || newStatus === "Completed";
        await updateStatus({
          variables: {
            shift_id: signup.shift_id,
            event_attendee_id: signup.event_attendee_id,
            checked_in: checkedIn,
            completed: false,
          },
        });
        await refetchVolunteerSignups();
        setMenuOpenId(null);
      } catch (err) {
        console.error(err);
      }
    };

    return (
      <div style={{ position: "relative" }}>
        <div
          ref={ref}
          onClick={toggleMenu}
          onKeyDown={(e) => e.key === "Enter" && toggleMenu()}
          role="button"
          tabIndex={0}
          style={{ cursor: "pointer", display: "inline-block" }}
        >
          <Label color={getStatusColor(currentStatus)}>
            <Text tw="font-bold">{currentStatus}</Text>
          </Label>
        </div>

        {isOpen && (
          <DropdownMenu style={{ left: anchorPos.left + 116 }}>
            {dropdownLabels.map((label) => (
              <DropdownItem
                key={label}
                onClick={() => handleStatusChange(label)}
                selected={label === currentStatus}
              >
                <Label color={getStatusColor(label)}>
                  <Text type="body2" tw="font-bold">
                    {label}
                  </Text>
                </Label>
              </DropdownItem>
            ))}
          </DropdownMenu>
        )}
      </div>
    );
  };

  const rows = useMemo(() => {
    if (!shiftSignupsData) return [];

    return [...shiftSignupsData.volunteerShiftSignups]
      .sort((a, b) =>
        a.event_attendee.user.name.localeCompare(b.event_attendee.user.name)
      )
      .map((signup) => ({
        name: signup.event_attendee.user.name,
        status: (
          <StatusCell
            signup={signup}
            menuOpenId={menuOpenId}
            setMenuOpenId={setMenuOpenId}
          />
        ),
        actions: (
          <div tw="flex flex-row w-full justify-end">
            <Button
              color="secondary"
              onClick={() =>
                handleDeleteVolunteer(signup.shift_id, signup.event_attendee_id)
              }
              tw="font-bold"
            >
              <Text tw="text-text-danger">Unregister</Text>
            </Button>
          </div>
        ),
      }));
  }, [shiftSignupsData, handleDeleteVolunteer, menuOpenId]);

  if (!volunteerShiftData || error || loading || !volunteerShift) {
    return null;
  }

  return (
    <StyledModal
      height="auto"
      width="781px"
      scroll
      isOpen={open}
      onClose={handleCloseModal}
    >
      <Text type="h3">{volunteerShift.shift.name}</Text>
      <Spacer height={24} />
      <InformationContainer>
        <InformationContainerRow>
          <div>
            <InformationText>
              {isSameDay ? "Date" : "Start Date"}
            </InformationText>
            <Spacer height={8} />
            <Text type="body1">{startDateStr}</Text>
          </div>

          <div>
            <InformationText>Time Start</InformationText>
            <Spacer height={8} />
            <Text type="body1">{startTimeStr}</Text>
          </div>

          {!isSameDay && (
            <div>
              <InformationText>End Date</InformationText>
              <Spacer height={8} />
              <Text type="body1">{endDateStr}</Text>
            </div>
          )}
          <div>
            <InformationText>Time End</InformationText>
            <Spacer height={8} />
            <Text type="body1">
              {showPersonalBuffer ? effectiveEndTimeStr : endTimeStr}
            </Text>
            {showPersonalBuffer && (
              <>
                <Spacer height={4} />
                <BufferNote>
                  A {BUFFER_MINUTES}-minute buffer is added to ensure sufficient
                  transition time since you don&apos;t have an immediate
                  subsequent shift. <BufferLearnMoreLink />
                </BufferNote>
              </>
            )}
            {isOrganizer && (
              <>
                <Spacer height={4} />
                <BufferNote>
                  A {BUFFER_MINUTES}-minute buffer is added to ensure sufficient
                  transition time when the volunteer doesn&apos;t have an
                  immediate subsequent shift. Calendar times stay {endTimeStr}.{" "}
                  <BufferLearnMoreLink />
                </BufferNote>
              </>
            )}
          </div>

          {isSameDay && (
            <div>
              <InformationText>Location</InformationText>
              <Spacer height={8} />
              <Text type="body1">{volunteerShift.shift.location}</Text>
            </div>
          )}
        </InformationContainerRow>

        <InformationContainerRow>
          <div>
            <InformationText>Type</InformationText>
            {!PRIMARY_SHIFTS.includes(volunteerShift.category_name) && (
              <Label color="secondary-light" tw="font-bold">
                Supplementary
              </Label>
            )}
            <Spacer height={8} />
            <Label color="secondary-light" tw="font-bold">
              {volunteerShift.category_name}
            </Label>
          </div>

          {!isSameDay && (
            <div>
              <InformationText>Location</InformationText>
              <Spacer height={8} />
              <Text type="body1">{volunteerShift.shift.location}</Text>
            </div>
          )}

          <div>
            <InformationText>Required Volunteers</InformationText>
            <Spacer height={8} />
            <Text type="body1">
              {parseMaxCountRestriction(volunteerShift.shift.restrictions)}
            </Text>
          </div>

          <div>
            <InformationText>Shift Lead</InformationText>
            <Spacer height={8} />
            <Text type="body1">
              {volunteerShift.organizer_responsible?.name}
            </Text>
          </div>
        </InformationContainerRow>
      </InformationContainer>
      <InformationContainerRow>
        <div>
          <Spacer height={24} />
          <InformationText>Shift Description</InformationText>
          <Spacer height={8} />
          <Text type="body1">{volunteerShift.description}</Text>
        </div>
      </InformationContainerRow>
      <Spacer height={40} />
      {!isOrganizer &&
        (() => {
          const capacity = parseMaxCountRestriction(
            volunteerShift.shift.restrictions
          );
          const filled = filledSpots;
          const isFull =
            capacity > 0 && filled !== undefined && filled >= capacity;
          const percent =
            capacity > 0 ? Math.min(100, ((filled ?? 0) / capacity) * 100) : 0;

          return (
            <SignupContainer>
              <InformationText>Volunteers</InformationText>
              <Spacer height={8} />
              <Text type="body1">
                {filled === undefined
                  ? availabilityError
                    ? "Availability unavailable"
                    : "Loading availability..."
                  : `${filled} of ${capacity || "—"} spots filled`}
                {isFull ? " · full" : ""}
                {isSignedUp ? " · you're on this shift" : ""}
              </Text>
              <CapacityBar>
                <CapacityFill percent={percent} isFull={isFull} />
              </CapacityBar>
              {(onRegister || onUnregister) && (
                <>
                  <Spacer height={16} />
                  {isSignedUp ? (
                    <Button
                      color="secondary-danger"
                      size="md"
                      onClick={onUnregister}
                    >
                      Unregister
                    </Button>
                  ) : isLocked ? (
                    <>
                      <Button color="primary" size="md" disabled>
                        Register
                      </Button>
                      {lockReason && (
                        <LockReasonText>{lockReason}</LockReasonText>
                      )}
                    </>
                  ) : (
                    <Button color="primary" size="md" onClick={onRegister}>
                      Register
                    </Button>
                  )}
                </>
              )}
            </SignupContainer>
          );
        })()}
      {isOrganizer && (
        <VolunteersContainer>
          <InformationText>Volunteers</InformationText>

          <StyledTable columns={columns} data={rows} showFooter />

          <AssignVolunteerContainer>
            <div tw="flex justify-between max-w-[480px] w-full bg-red-50">
              <Autocomplete
                options={userOptions ?? []}
                handleSelect={(value) => setVolunteerId(Number(value))}
                handleTextInputChange={(value) => {
                  setSearchQuery(value);
                  debouncedSearchUsers(value);
                }}
                renderInput={(props) => (
                  <TextInput
                    type="body1"
                    tw="text-text-tertiary font-medium w-full h-48"
                    onChange={props.onChange}
                    placeholder="Enter volunteer email or name"
                    value={props.value}
                  />
                )}
              />
            </div>

            <div tw="w-full h-full! flex justify-end items-center">
              <Button
                size="lg"
                color="primary"
                tw="w-full h-full font-bold"
                onClick={handleAddVolunteer}
                disabled={volunteerId === null || isAssigning}
              >
                {isAssigning ? "Assigning..." : "Assign Volunteer"}
              </Button>
            </div>
          </AssignVolunteerContainer>
        </VolunteersContainer>
      )}
      <Spacer height={24} />
      {isOrganizer && (
        <FooterActionsRow>
          <RegistrationToggle role="group" aria-label="Shift registration">
            <Text type="body1">
              {volunteerShift.registration_open
                ? "Registration open"
                : "Registration closed"}
            </Text>
            <ToggleSlider
              variant="light"
              isOn={!!volunteerShift.registration_open}
              disabled={updatingRegistration}
              aria-label={
                volunteerShift.registration_open
                  ? "Close registration"
                  : "Open registration"
              }
              onToggle={handleToggleRegistration}
            />
          </RegistrationToggle>
          <Button
            color="secondary"
            onClick={() => {
              navigate("/volunteer/shifts/edit?shift_id=" + shiftId);
            }}
          >
            <Text>{"Edit Shift"}</Text>
          </Button>
        </FooterActionsRow>
      )}
      <Modal
        isOpen={!!pendingConflict}
        onClose={clearConflictPrompt}
        width="480px"
        height="auto"
      >
        {pendingConflict &&
          (confirmRemoveConflict ? (
            <>
              <Text type="h3">Are you sure?</Text>
              <Spacer height={16} />
              <Text type="body1">
                This will unregister{" "}
                <strong>{pendingConflict.volunteerName}</strong> from the
                conflicting{" "}
                {pendingConflict.conflicts.length === 1 ? "shift" : "shifts"}{" "}
                below, then assign them to this shift.
              </Text>
              <ConflictList>
                {pendingConflict.conflicts.map((conflict) => (
                  <li key={conflict.shiftId}>
                    {formatConflictShift(conflict)}
                  </li>
                ))}
              </ConflictList>
              <Spacer height={24} />
              <ConflictActions>
                <Button
                  color="secondary"
                  size="md"
                  onClick={clearConflictPrompt}
                  disabled={isAssigning}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  size="md"
                  onClick={handleConfirmRemoveConflicts}
                  disabled={isAssigning}
                >
                  {isAssigning ? "Assigning..." : "Yes, remove and assign"}
                </Button>
              </ConflictActions>
            </>
          ) : (
            <>
              <Text type="h3">Shift conflict</Text>
              <Spacer height={16} />
              <Text type="body1">
                <strong>{pendingConflict.volunteerName}</strong> already has{" "}
                {pendingConflict.conflicts.length === 1
                  ? "a conflicting shift"
                  : "conflicting shifts"}
                . You can still assign them here, or remove the conflicting{" "}
                {pendingConflict.conflicts.length === 1 ? "shift" : "shifts"}{" "}
                first.
              </Text>
              <ConflictList>
                {pendingConflict.conflicts.map((conflict) => (
                  <li key={conflict.shiftId}>
                    {formatConflictShift(conflict)}
                  </li>
                ))}
              </ConflictList>
              <Spacer height={24} />
              <ConflictActions>
                <Button
                  color="secondary"
                  size="md"
                  onClick={clearConflictPrompt}
                  disabled={isAssigning}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  size="md"
                  onClick={() => setConfirmRemoveConflict(true)}
                  disabled={isAssigning}
                >
                  Remove and assign
                </Button>
                <Button
                  color="primary"
                  size="md"
                  onClick={handleAssignDespiteConflicts}
                  disabled={isAssigning}
                >
                  {isAssigning ? "Assigning..." : "Assign anyway"}
                </Button>
              </ConflictActions>
            </>
          ))}
      </Modal>
    </StyledModal>
  );
};

const FooterActionsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const RegistrationToggle = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const InformationContainerRow = styled.div`
  display: flex;
  gap: 64px;

  @media (max-width: ${deviceBreakpoints.tablet}px) {
    flex-direction: column;
    gap: 24px;
  }
`;

const InformationContainer = styled.section`
  display: flex;
  flex-direction: column;
  gap: 24px;

  @media (max-width: ${deviceBreakpoints.tablet}px) {
    flex-direction: row;
    gap: 64px;
  }
`;

const VolunteersContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const AssignVolunteerContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;

  @media (max-width: ${deviceBreakpoints.tablet}px) {
    flex-direction: column;
    align-items: flex-end;
  }
`;

const InformationText = styled.p`
  font-weight: bold;
  font-size: 20px;
  color: #0e3460;
`;

const StyledTable = styled(Table)`
  overflow: visible;
`;

const StyledModal = styled(Modal)`
  overflow-y: auto;
  max-height: 90vh;
  padding-bottom: 24px;
`;

const DropdownItem = styled.div<{ selected: boolean }>`
  padding: 8px 12px;
  cursor: pointer;
  background-color: ${({ selected }) => (selected ? "#f9fafb" : "#fff")};
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: #f9fafb;
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  background-color: #fff;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  z-index: 9999;
  transform: translateY(-65%);
  overflow: hidden;
`;

export default VolunteerShiftModal;

const SignupContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-bottom: 24px;
`;

const CapacityBar = styled.div`
  width: 100%;
  max-width: 320px;
  height: 7px;
  margin-top: 8px;
  border-radius: 20px;
  background: ${({ theme }) => theme.globalConstants.color.backgroundGray};
  overflow: hidden;
`;

const LockReasonText = styled.p`
  margin-top: 8px;
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.globalConstants.color.textTertiary};
`;

const CapacityFill = styled.div<{ percent: number; isFull: boolean }>`
  height: 100%;
  border-radius: 20px;
  width: ${({ percent }) => percent}%;
  background: ${({ theme, isFull }) =>
    isFull
      ? theme.globalConstants.color.backgroundDanger
      : theme.globalConstants.color.backgroundSuccess};
`;

const BufferNote = styled.p`
  max-width: 280px;
  margin: 0;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.35;
  color: ${({ theme }) => theme.globalConstants.color.textTertiary};
`;

const ConflictActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap;
`;

const ConflictList = styled.ul`
  margin: 8px 0 0;
  padding-left: 20px;
`;
